import React, { useState, useEffect, useRef } from 'react';
import API_BASE from '../config';

/**
 * Small "pick from contacts" affordance for a phone-number input — used on
 * the Dialpad and the Messages "New Message" screen. Contacts are scoped to
 * the selected Line (see contacts_model.py), so this is disabled until a
 * line is chosen, same as the Contacts tab itself.
 */
export default function ContactPicker({ selectedNumber, authToken, onSelect }) {
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open || !selectedNumber || !authToken) return;
    let cancelled = false;
    fetch(`${API_BASE}/contacts?line=${encodeURIComponent(selectedNumber)}`, {
      headers: { Authorization: `Bearer ${authToken}`, 'ngrok-skip-browser-warning': 'true' },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => { if (!cancelled) setContacts(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setContacts([]); });
    return () => { cancelled = true; };
  }, [open, selectedNumber, authToken]);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const filtered = contacts.filter((c) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (c.name || '').toLowerCase().includes(q) || (c.number || '').includes(q);
  });

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={!selectedNumber}
        title={selectedNumber ? 'Choose from contacts' : 'Select a Line first'}
        aria-label="Choose from contacts"
        style={{
          border: 'none',
          background: 'none',
          fontSize: 18,
          cursor: selectedNumber ? 'pointer' : 'not-allowed',
          padding: '6px 8px',
          opacity: selectedNumber ? 1 : 0.35,
          color: 'inherit',
        }}
      >
        👤
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            zIndex: 30,
            width: 260,
            maxHeight: 320,
            overflowY: 'auto',
            background: 'var(--truva-surface)',
            border: '1px solid var(--truva-border)',
            borderRadius: 10,
            boxShadow: 'var(--truva-elevation-e1)',
            padding: 8,
          }}
        >
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search contacts"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '6px 8px',
              borderRadius: 6,
              border: '1px solid var(--truva-border)',
              marginBottom: 6,
              fontSize: 15,
              background: 'transparent',
              color: 'inherit',
            }}
          />
          {filtered.length === 0 ? (
            <div style={{ fontSize: 14, opacity: 0.6, padding: '8px 4px' }}>No contacts</div>
          ) : filtered.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => { onSelect(c.number); setOpen(false); setQuery(''); }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '6px 4px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                color: 'inherit',
                fontSize: 15,
                borderRadius: 6,
              }}
            >
              <div style={{ fontWeight: 600 }}>{c.name}</div>
              <div style={{ fontSize: 13, opacity: 0.7 }}>{c.number}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
